import { LiveBanner } from "../features/live/LiveBanner";
import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../features/auth/AuthContext";
import { Button } from "./Button";
import { UserMenu } from "./UserMenu";
import { SupportProvider } from "../features/support/SupportContext";
import { CoffeeIconButton, SupportLink } from "../features/support/SupportPrompts";
import { isFreeMode, useSiteConfig } from "../lib/site-config";
// Listens for the browser's install offer from the first page load.
import "../lib/install";
import { ShareRaceTraceButton } from "../features/share/ShareRaceTrace";
import { noteShareArrival } from "../features/share/shareLink";
import { noteAlertArrival } from "../features/my-cars/alerts";
import { FullscreenButton, useFullscreen } from "../lib/fullscreen";
import { useIsCompact } from "../lib/breakpoint";
import { AppBar } from "./AppBar";
import { HELP_URL, HelpButton } from "../features/help/HelpButton";

/** RaceTrace's links for the shared profile menu. */
function RaceTraceUserMenu() {
  const { user } = useAuth();
  return (
    <UserMenu
      afterLogout="/"
      groups={[
        [
          { label: "Dashboard", to: "/dashboard" },
          { label: "My cars", to: "/settings/cars" },
          { label: "My drivers", to: "/settings/drivers" },
        ],
        [
          { label: "Settings", to: "/settings/account" },
          { label: "Billing", to: "/settings/billing" },
          { label: "Help", to: HELP_URL, external: true },
        ],
        user?.role === "ADMIN" ? [{ label: "Admin Panel", to: "/admin" }] : [],
      ]}
    />
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    // From 640 px to 1024 px; phones use the bottom bar's Me place instead.
    <div className="hidden sm:block lg:hidden">
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        aria-label="Menu"
      >
        {open ? (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {open && (
        <div className="absolute top-16 left-0 right-0 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 shadow-lg z-50">
          <nav className="container-page py-4 flex flex-col gap-2">
            {isAuthenticated && (
              <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-gray-700 dark:text-gray-300">
                Dashboard
              </Link>
            )}
            {isAuthenticated && (
              <Link to="/races" onClick={() => setOpen(false)} className="py-2 text-gray-700 dark:text-gray-300">
                Events
              </Link>
            )}
            {!isAuthenticated && !isFreeMode() && (
              <Link to="/pricing" onClick={() => setOpen(false)} className="py-2 text-gray-700 dark:text-gray-300">
                Pricing
              </Link>
            )}
            <SupportLink variant="menu" onClick={() => setOpen(false)} />
            <ShareRaceTraceButton source="menu" className="py-2 text-left text-gray-700 dark:text-gray-300" onShared={() => setOpen(false)} />

            {isAuthenticated ? (
              <>
                {user?.role === "ADMIN" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-gray-700 dark:text-gray-300">
                    Admin
                  </Link>
                )}
                <button
                  onClick={async () => {
                    setOpen(false);
                    await logout();
                    navigate("/");
                  }}
                  className="py-2 text-left text-red-600 dark:text-red-400"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-gray-700 dark:text-gray-300">
                  Log in
                </Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="py-2 text-brand-600 dark:text-brand-400 font-medium">
                  Sign up
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </div>
  );
}

/** A top-bar link; the current section is shown in the accent colour. */
function NavItem({ to, label }: { to: string; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(`${to}/`) || (to === "/races" && pathname === "/chart");
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={
        active
          ? "text-sm font-medium text-gray-900 dark:text-gray-50 underline decoration-brand-500 decoration-2 underline-offset-[22px]"
          : "text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      }
    >
      {label}
    </Link>
  );
}

export function Layout() {
  // A visit from a shared link (?from=share) is counted once, and the tag removed.
  useEffect(() => {
    noteShareArrival((path) => (path === "/" ? "home" : "app"));
    // And a visit from a race-ready email (?from=alert).
    noteAlertArrival();
  }, []);
  const { isAuthenticated, isLoading, user } = useAuth();
  useSiteConfig(); // re-render when free mode / support settings arrive
  // Race, live race and qualifying pages are apps: they fill the window exactly (no page scroll,
  // no footer) and each part scrolls on its own.
  const { pathname } = useLocation();
  const appPage = pathname === "/chart" || pathname.startsWith("/live/") || pathname.startsWith("/qualifying/");
  // Full screen: the browser's bars and the taskbar hidden, and this top bar and the live banner too.
  const full = useFullscreen();
  // Phones (mobile redesign, PR 2): a race screen fills the screen with its own one-line header
  // and back arrow, so the site bar and live banner step aside; every other page gets the
  // bottom bar (AppBar) in place of the hamburger menu.
  const compact = useIsCompact();
  // A race screen: a race, a live session (not the list or a pre-race page) or a qualifying session.
  const raceScreen = compact && (pathname === "/chart" || /^\/live\/(?!next\/)[^/]+$/.test(pathname) || /^\/qualifying\/[^/]+$/.test(pathname));
  const showBar = compact && !raceScreen && !full;

  return (
    <SupportProvider>
    <div className={appPage ? `h-dvh flex flex-col overflow-hidden${showBar ? "" : " pb-safe"}` : "min-h-screen flex flex-col"}>
      {/* Navigation */}
      {full && <FullscreenButton on floating />}
      <header className={`${full || raceScreen ? "hidden " : ""}sticky top-0 z-40 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800`}>
        <nav className="container-page flex items-center justify-between h-14 sm:h-16 gap-2 sm:gap-3" aria-label="Main">
          {/* The same brand block as Finding Grip's header (GripLayout.tsx): Tedder logo | app mark + name.
              Phones get a smaller Tedder logo and tighter spacing so the name and coffee button fit. */}
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 md:shrink-0">
              {/* Phones: the RaceTrace mark and name only (the Tedder logo is on the laptop bar and in Me). */}
              <a href="https://tedderengineering.com" target="_blank" rel="noopener noreferrer" className="hidden shrink-0 sm:block">
                <img src="/te-logo-black.png" alt="Tedder Engineering" className="h-6 sm:h-8 dark:hidden" />
                <img src="/te-logo-white.png" alt="Tedder Engineering" className="h-6 sm:h-8 hidden dark:block" />
              </a>
              <Link
                to={isAuthenticated ? "/dashboard" : "/"}
                title={isAuthenticated ? "RaceTrace: back to Home" : "RaceTrace: the home page"}
                className="flex min-h-[44px] items-center gap-2 sm:border-l border-gray-300 dark:border-gray-700 sm:pl-3 text-lg font-bold text-gray-900 dark:text-gray-100 min-w-0"
              >
                <img src="/racetrace-icon-192.png" alt="" className="h-6 w-6 shrink-0 rounded-md" />
                <span className="truncate">RaceTrace</span>
              </Link>
            </div>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-4">
              {isAuthenticated && <NavItem to="/dashboard" label="Dashboard" />}
              {isAuthenticated && <NavItem to="/races" label="Events" />}
              {user?.role === "ADMIN" && <NavItem to="/admin" label="Admin" />}
              {!isAuthenticated && !isFreeMode() && (
                <Link
                  to="/pricing"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Pricing
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Below 1024px, as on Finding Grip: the compact header with the coffee entry as an icon. */}
            <FullscreenButton on={full} className="hidden sm:flex" />
            {/* Help, at the bar's right end on every screen size (phones: before the coffee). */}
            <HelpButton className="lg:order-last" />
            <CoffeeIconButton className="hidden sm:grid lg:hidden" />
            {/* Desktop auth */}
            <div className="hidden lg:flex items-center gap-3">
              <SupportLink />
              {isLoading ? (
                <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
              ) : isAuthenticated ? (
                <RaceTraceUserMenu />
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  >
                    Log in
                  </Link>
                  <Link to="/signup">
                    <Button size="sm">Sign up</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu */}
            <MobileMenu />
          </div>
        </nav>
      </header>
      {/* Phones: the bottom bar's Live place (with its red dot) says a race is live instead. */}
      {!full && !compact && <LiveBanner />}

      {/* Page content */}
      <main className={appPage ? "flex-1 min-h-0" : "flex-1"}>
        <Outlet />
      </main>

      {/* Footer */}
      {/* Phones: the bottom bar's Me holds share, terms and privacy instead. */}
      {!appPage && !showBar && <footer className="border-t border-gray-200 dark:border-gray-800 py-8">
        <div className="container-page flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
          <a href="https://tedderengineering.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/te-logo-black.png" alt="Tedder Engineering" className="h-6 dark:hidden" />
            <img src="/te-logo-white.png" alt="Tedder Engineering" className="h-6 hidden dark:block" />
            <p>&copy; {new Date().getFullYear()} Tedder Engineering. All rights reserved.</p>
          </a>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <ShareRaceTraceButton source="footer" className="hover:text-gray-700 dark:hover:text-gray-300" />
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">
              Privacy
            </Link>
            <a href={HELP_URL} className="hover:text-gray-700 dark:hover:text-gray-300">
              Help
            </a>
          </div>
        </div>
      </footer>}

      {showBar && <AppBar />}
    </div>
    </SupportProvider>
  );
}
